<template>
  <div class="home-view">

    <!-- Hero -->
    <div class="hero" :style="heroStyle">
      <div class="hero-content">
        <h1>{{ config.content.heroTitle }}</h1>
        <span class="hero-btn-wrap mx-3">
          <router-link to="/transfer" class="ube-l-btn">
            Transfer Completion Plan
          </router-link>
          <span class="hero-tooltip">Already completed some community college coursework? Enter the courses you've taken and choose a bachelor's degree at {{ config.branding.shortName }}. We'll show how your credits transfer and the courses you still need to complete your bachelor's degree.</span>
        </span>
        <span v-if="twoToFourYearEnabled" class="hero-btn-wrap mx-3">
          <router-link to="/TwoYeartoFourYear" class="ube-l-btn-secondary">
            Associate to Bachelor's Degree
          </router-link>
          <span class="hero-tooltip">Plan your journey from community college to {{ config.branding.shortName }}. Choose an associate degree and a bachelor's degree to see a term by term plan for completing both programs.</span>
        </span>
      </div>
    </div>

    <!-- How It Works -->
    <section class="hiw" id="how">
      <div class="hiw-wrap">
        <div class="hiw-head">
          <h2>How it works</h2>
          <div class="hiw-rule"></div>
          <p>{{ hiwSubtitle }}</p>
        </div>

        <div class="hiw-grid" :class="{ 'hiw-grid--single': !twoToFourYearEnabled }">

          <!-- Path 1 column (conditional) -->
          <div v-if="twoToFourYearEnabled" class="hiw-col hiw-col--primary">
            <div class="hiw-col-head">
              <div class="hiw-col-pin">
                <svg class="icn" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M2 9l10-4 10 4-10 4L2 9z"/>
                  <path d="M6 11v4c0 1.5 2.7 3 6 3s6-1.5 6-3v-4"/>
                </svg>
              </div>
              <div>
                <span class="hiw-col-label">If you click</span>
                <h3>Associate to Bachelor's Degree</h3>
              </div>
            </div>
            <div class="hiw-steps">
              <div class="hiw-step">
                <div class="hiw-step-num">1</div>
                <div>
                  <h4>Pick the associate degree you want to earn</h4>
                  <p>Choose the community college you'll attend and the associate degree you plan to complete.</p>
                </div>
              </div>
              <div class="hiw-step">
                <div class="hiw-step-num">2</div>
                <div>
                  <h4>Pick a bachelor's program at {{ config.branding.shortName }}</h4>
                  <p>Select the program you want to transfer to after earning your associate degree.</p>
                </div>
              </div>
              <div class="hiw-step">
                <div class="hiw-step-num">3</div>
                <div>
                  <h4>See your degree plan</h4>
                  <p>View a term-by-term plan for earning both your associate and bachelor's degree.</p>
                </div>
              </div>
            </div>
            <div class="hiw-col-foot">
              <div class="hiw-for">
                <strong>Best for</strong>
                Students who plan to earn an associate degree at a community college and then complete a bachelor's degree at {{ config.branding.shortName }}.
              </div>
              <router-link to="/TwoYeartoFourYear" class="hiw-start hiw-start--primary">Start</router-link>
            </div>
          </div>

          <!-- Path 2 column -->
          <div class="hiw-col hiw-col--secondary">
            <div class="hiw-col-head">
              <div class="hiw-col-pin">
                <svg class="icn" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="4" y="3" width="16" height="18" rx="2"/>
                  <path d="M8 8l2 2 4-4"/>
                  <path d="M8 15h8"/>
                  <path d="M8 18h5"/>
                </svg>
              </div>
              <div>
                <span class="hiw-col-label">If you click</span>
                <h3>Transfer Completion Plan</h3>
              </div>
            </div>
            <div class="hiw-steps">
              <div class="hiw-step">
                <div class="hiw-step-num">1</div>
                <div>
                  <h4>Select your community college</h4>
                  <p>Tell us where you've been taking courses.</p>
                </div>
              </div>
              <div class="hiw-step">
                <div class="hiw-step-num">2</div>
                <div>
                  <h4>Add the courses</h4>
                  <p>Select the courses from the provided list that you've already finished or are currently taking.</p>
                </div>
              </div>
              <div class="hiw-step">
                <div class="hiw-step-num">3</div>
                <div>
                  <h4>Pick your bachelor's degree</h4>
                  <p>Choose the major you want to transfer to. We'll show you a term-by-term plan that includes how your courses transfer and the remaining courses you will need.</p>
                </div>
              </div>
            </div>
            <div class="hiw-col-foot">
              <div class="hiw-for">
                <strong>Best for</strong>
                Students who've taken some community college courses and want to see how those courses apply to a bachelor's degree at {{ config.branding.shortName }}.
              </div>
              <router-link to="/transfer" class="hiw-start hiw-start--secondary">Start</router-link>
            </div>
          </div>

        </div>
      </div>
    </section>

  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import { useTenantConfig } from "@/composables/useTenantConfig";
import { useFeatureFlags } from "@/composables/useFeatureFlags";

export default defineComponent({
  name: "HomeView",
  setup() {
    const { config } = useTenantConfig();
    const { twoToFourYearEnabled } = useFeatureFlags();

    const heroImageUrl = computed(() => {
      return new URL(`../assets/${config.branding.heroImage}`, import.meta.url).href;
    });

    const heroStyle = computed(() => ({
      backgroundImage: `url(${heroImageUrl.value})`,
    }));

    const hiwSubtitle = computed(() =>
      twoToFourYearEnabled.value
        ? "Each tool follows a different flow because each one answers a different question. Here's exactly what happens when you click either button above."
        : "Here's exactly what happens, step by step, when you start your transfer evaluation."
    );

    return {
      config,
      twoToFourYearEnabled,
      heroStyle,
      hiwSubtitle,
    };
  },
});
</script>
